import { App, Route, UniversalAppState } from "@pimo/pimo-app-builder";
import { DefaultOverlayPartialAppState, PMPopUp } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import { Project } from "product-test-types";
import {
  CHARACTER_LIMITATIONS,
  getFieldValidation,
  validateBasicProjectForm,
} from "product-test-utils";
import { generatePath } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { TestAppState } from "../app";
import {
  getFormValuesFromEventPayload,
  isCustomButtonEventPayload,
  PimoFormDialog,
} from "../components/pimo-form";
import { APP_ROUTES } from "../constants";
import { fetchEntityConfiguration } from "../helpers/fetch-entity-configurations";
import { fetchOverlay } from "../helpers/fetch-overlay";
import { fetchOverview } from "../helpers/fetch-overview";
import {
  createProject,
  fetchProject,
  updateProject,
} from "../helpers/fetch-project";
import { fetchSettings, fetchSetupSteps } from "../helpers/fetch-settings";
import { showSnackbar } from "../helpers/show-snackbar";
import { toDropdownItems } from "../utils/dropdown-options";
import {
  getCreateEntityLabel,
  getEditEntityLabel,
} from "../utils/get-add-new-label";
import { getTotalAndCompletedSteps } from "../utils/get-total-and-completed-steps";

const STYLE_TEXT = {
  "& .MuiInputBase-input": {
    fontSize: "14px",
  },
  // multiline textarea
  "& .MuiInputBase-inputMultiline, & textarea": {
    fontSize: "14px",
  },
  // label size (optional)
  "& .MuiInputLabel-root": {
    fontSize: "14px",
  },
  fontSize: "14px",
  width: "100%",
  borderRadius: "6px",
  height: "45px",
};

export async function buildCreateProjectFormRoute(
  app: App<TestAppState>,
  parentRoute: Route<"", TestAppState>,
  isFromOverview: boolean
) {
  const dialog = app.createView({
    name: "add-project",
  });
  let projectId: string | undefined;
  let createProjectDraft: Project | undefined;
  const projectForm = new PimoFormDialog<Project>();

  const form = dialog.addComponent({ component: projectForm });

  registerFormEvents();
  createConfirmationDialog();

  function getCreateProjectDraft() {
    if (createProjectDraft) {
      return createProjectDraft;
    }

    const today = new Date().toISOString().split("T")[0];
    createProjectDraft = {
      id: uuidv4(),
      flag: "create",
      startDate: today,
      endDate: today,
      userCreatedProject: true,
    } as Project;

    return createProjectDraft;
  }

  function registerFormEvents() {
    const language = app.getAppState().language || "en";
    form.on("submit", async ({ payload }) => {
      if (isCustomButtonEventPayload(payload) && !payload.hasChanges) {
        closeEditForm();
        return;
      }

      const submittedProject = getFormValuesFromEventPayload<Project>(payload);
      if (!submittedProject) {
        return;
      }

      let createdProjectId: string | undefined = undefined;

      try {
        if (submittedProject.flag !== "create") {
          await updateProject(submittedProject.id, submittedProject, false);
        } else {
          createdProjectId = (await createProject(submittedProject)).id;
        }
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : getLocalizedMessage(language, "errorOccurred") ||
              "Saving failed.";

        showSnackbar(
          app as unknown as App<
            UniversalAppState & DefaultOverlayPartialAppState
          >,
          message,
          "error"
        );
        return;
      }

      const [overlay, overview, currentReport, completedSetupSteps, settings] =
        await Promise.all([
          await fetchOverlay(),
          await fetchOverview(),
          await fetchProject({
            id: app.getAppState().currentReport?.project?.id ?? "",
          }),
          fetchSetupSteps(),
          fetchSettings(),
        ]);

      const { totalSteps, completedSteps } =
        getTotalAndCompletedSteps(completedSetupSteps);

      app?.patchAppState({
        overlay,
        overview,
        currentReport,
        recentlyCreatedProjectId:
          submittedProject.flag === "create" ? createdProjectId : null,

        welcomeScreenStatus: {
          steps: completedSetupSteps,
          totalSteps,
          completedSteps,
        },
        settings: { ...app.getAppState().settings, ...settings },
      });

      showSnackbar(
        app as unknown as App<
          UniversalAppState & DefaultOverlayPartialAppState
        >,
        getLocalizedMessage(language, "allChangesSaved") ||
          "All changes have been saved.",
        "success"
      );
      if (submittedProject.flag === "create" && createdProjectId) {
        app.navigate(
          generatePath(APP_ROUTES.report, {
            id: createdProjectId,
            viewname: "tracking-item",
          })
        );
      }
    });

    form.on("cancel", () => closeEditForm());
    form.on("cancel-with-changes", () =>
      app.patchAppState({ showEditFormConfirmationDialog: true })
    );
    form.on("close", () => closeEditForm());
  }
  function closeEditForm() {
    const resourceLockPlugin =
      app.getPluginByName<ResourceLockPlugin<TestAppState>>(
        "ResourceLockPlugin"
      );

    app.patchAppState({
      editForm: {},
    });

    resourceLockPlugin?.unlock(`project-${projectId}`);

    isFromOverview
      ? app.navigate(APP_ROUTES.overview)
      : app.navigate(
          generatePath(APP_ROUTES.report, {
            id: projectId ?? "",
            viewname: "dashboard",
          })
        );
  }

  const route = parentRoute.createChildRoute<"id" | "isFromOverview">({
    path: APP_ROUTES.projectDialog,
    view: dialog,
    isOverlayChild: true,
  });

  function createConfirmationDialog() {
    const confirmationDialog = dialog.addComponent({
      component: PMPopUp,
    });

    confirmationDialog.mapVisibility(
      ({ showEditFormConfirmationDialog }) =>
        showEditFormConfirmationDialog === true
    );

    confirmationDialog.mapState(() => {
      const language = app.getAppState().language || "en";
      return {
        title:
          getLocalizedMessage(language, "dialog.title.unsavedChanges", {
            entity: "form",
          }) || "Unsaved Changes",
        description:
          getLocalizedMessage(language, "dialog.message.unsavedChanges", {
            entity: "form",
          }) ||
          "You have made changes that have not been saved. If you close the settings, these changes will be lost.",
        maxWidth: "sm",

        cancelButtonText:
          getLocalizedMessage(language, "dialog.button.cancel") || "Cancel",
        cancelButtonColor: "white",
        confirmationButtonColor: "blue",
        confirmationButtonText:
          getLocalizedMessage(language, "dialog.button.continue") || "Continue",

        cancelButtonFireEvent: () => closeConfirmationDialog(),
        confirmationButtonFireEvent: () => {
          closeConfirmationDialog();
          closeEditForm();
        },
        onClose: () => closeConfirmationDialog(),
      };
    });

    function closeConfirmationDialog() {
      app.patchAppState({
        showEditFormConfirmationDialog: false,
      });
    }
  }

  route.on("load", async (payload) => {
    isFromOverview = payload.payload?.parameters?.isFromOverview === "true";
    projectId = payload.payload?.parameters?.id;
    const currentProject =
      projectId && projectId !== "new-project"
        ? await fetchProject({ id: projectId })
        : undefined;
    createProjectDraft = currentProject?.project
      ? undefined
      : getCreateProjectDraft();
    const configuration = await fetchEntityConfiguration();

    app.patchAppState({
      currentReport: currentProject,
      editForm: {
        currentProject: currentProject?.project ?? createProjectDraft,
      },
      settings: {
        ...app.getAppState().settings,
        configuration: configuration,
      },
    });
    form.mapState((appState) => {
      const language = appState.language || "en";
      const projectNameKey = appState.settings?.configuration?.projectName;
      const currentProjectFormData =
        appState.editForm?.currentProject ?? getCreateProjectDraft();
      const isCreateMode =
        !appState.editForm?.currentProject ||
        appState.editForm.currentProject.flag === "create";

      return {
        ...{
          ...currentProjectFormData,
        },
        ...{
          validateFormData: (data: Omit<Project, "id">) =>
            validateBasicProjectForm(data, configuration, {
              fieldRequired:
                getLocalizedMessage(
                  language,
                  "validationErrors.fieldRequired"
                ) || "This field is required",
              dateNotValid:
                getLocalizedMessage(
                  language,
                  "validationErrors.dateNotValid"
                ) || "This date is not valid",
              endDateAfterStartDate:
                getLocalizedMessage(
                  language,
                  "validationErrors.endDateAfterStartDate"
                ) || "End date should be after start date",
            }),
        },
        title: isCreateMode
          ? getCreateEntityLabel(language, projectNameKey, "project")
          : getEditEntityLabel(language, projectNameKey, "project"),
        inline: true,
        enableCloseButton: true,
        customButtons: {
          left: [],
          right: [
            {
              id: "cancel",
              label:
                getLocalizedMessage(appState.language || "en", "cancel") ||
                "Cancel",
              eventName: "cancel",
              includeFormDataInPayload: true,
            },
            {
              id: "save-and-close",
              label:
                getLocalizedMessage(
                  appState.language || "en",
                  "saveAndClose"
                ) || "Save & Close",
              eventName: "submit",
              sx: { color: "white" },
              variant: "contained",
              validateBeforeEmit: true,
              includeFormDataInPayload: true,
            },
          ],
        },
      };
    });

    await projectForm.buildDynamicTabsAsynchronously(async () => {
      const appState = app.getAppState();
      const configuration = appState.settings?.configuration;
      const language = appState.language;

      const overallTab = projectForm.addFormTab({
        label: getLocalizedMessage(language, "summary") || "Summary",
        id: "summary",
      });

      overallTab.addFormField({
        type: "text",
        label: `${getLocalizedMessage(language, "name") || "Name"}${
          getFieldValidation(configuration, "name", "projectBasic") ? "*" : ""
        }`,
        multiline: false,
        name: "name",
        maxLength: {
          value: CHARACTER_LIMITATIONS["projectName"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["projectName"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["projectName"]} characters or less`,
        },
        required: {
          value: true,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        sx: STYLE_TEXT,
        uiOptions: {
          fieldSize: { xs: 6 },
        },
      });

      overallTab.addFormField({
        type: "text",
        label: `${getLocalizedMessage(language, "customId") || "Custom ID"}${
          getFieldValidation(configuration, "customId", "projectBasic")
            ? "*"
            : ""
        }`,
        multiline: false,
        name: "customId",
        maxLength: {
          value: CHARACTER_LIMITATIONS["projectCustomId"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["projectCustomId"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["projectCustomId"]} characters or less`,
        },
        required: {
          value: true,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        sx: STYLE_TEXT,
        uiOptions: {
          fieldSize: { xs: 6 },
        },
      });

      overallTab.addFormField({
        type: "rich-text",
        label: `${
          getLocalizedMessage(language, "description") || "Description"
        }${
          getFieldValidation(configuration, "description", "projectBasic")
            ? "*"
            : ""
        }`,
        multiline: true,
        name: "description",
        maxLength: {
          value: CHARACTER_LIMITATIONS["projectDescription"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["projectDescription"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["projectDescription"]} characters or less`,
        },
        required: {
          value: true,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        minRows: 3,
        sx: STYLE_TEXT,
        isVisible: () =>
          configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "projectBasic")
            ?.find((f) => f.fieldName === "description")?.enabled ?? false,
      });

      overallTab.addFormField({
        type: "text",
        label: `${getLocalizedMessage(language, "owner") || "Owner"}${
          getFieldValidation(configuration, "owner", "projectBasic") ? "*" : ""
        }`,
        multiline: false,
        name: "owner",
        maxLength: {
          value: CHARACTER_LIMITATIONS["projectOwner"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              { maxLength: CHARACTER_LIMITATIONS["projectOwner"] }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["projectOwner"]} characters or less`,
        },
        required: {
          value: true,
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.fieldRequired"
            ) || "This field is required",
        },
        sx: STYLE_TEXT,
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        isVisible: () =>
          configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "projectBasic")
            ?.find((f) => f.fieldName === "owner")?.enabled ?? false,
      });

      overallTab.addFormField({
        type: "number",
        label: `${
          getLocalizedMessage(language, "approvedBudget") || "Approved Budget"
        } (€)${
          getFieldValidation(configuration, "approvedBudget", "projectBasic")
            ? "*"
            : ""
        }`,
        multiline: false,
        name: "approvedBudget",
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        maxLength: {
          value: CHARACTER_LIMITATIONS["approvedBudget"],
          message:
            getLocalizedMessage(
              language || "en",
              "validationErrors.maxLength",
              {
                maxLength: CHARACTER_LIMITATIONS["approvedBudget"],
              }
            ) ||
            `Must be ${CHARACTER_LIMITATIONS["approvedBudget"]} characters or less`,
        },
        sx: STYLE_TEXT,
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "projectBasic")
              ?.find((f) => f.fieldName === "approvedBudget")?.enabled
          ),
      });

      overallTab.addFormField({
        type: "date",
        label: `${getLocalizedMessage(language, "startDate") || "Start Date"}${
          getFieldValidation(configuration, "startDate", "projectBasic")
            ? "*"
            : ""
        }`,
        name: "startDate",
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "projectBasic")
              ?.find((f) => f.fieldName === "startDate")?.enabled
          ),
      });
      overallTab.addFormField({
        type: "date",
        label: `${getLocalizedMessage(language, "endDate") || "End Date"}${
          getFieldValidation(configuration, "endDate", "projectBasic")
            ? "*"
            : ""
        }`,
        name: "endDate",
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "projectBasic")
              ?.find((f) => f.fieldName === "endDate")?.enabled
          ),
      });

      overallTab.addFormField({
        type: "dropdown",
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        label: `${
          configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "projectBasic")
            ?.find(
              (f) =>
                f.fieldName === "category" &&
                f.enabled === true &&
                (configuration?.categoryDefinition?.length ?? 0) > 0
            )?.label ??
          (getLocalizedMessage(language, "category") || "Category")
        }${
          getFieldValidation(configuration, "category", "projectBasic")
            ? "*"
            : ""
        }`,
        name: `category`,
        sx: STYLE_TEXT,
        menuItemsSx: STYLE_TEXT,
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "projectBasic")
              ?.find((f) => f.fieldName === "category")?.enabled
          ),
        items: toDropdownItems(configuration?.categoryDefinition),
      });

      overallTab.addFormField({
        type: "text",
        label: `${
          configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "projectBasic")
            ?.find((f) => f.fieldName === "freeTextFieldOne")?.label ??
          (getLocalizedMessage(language, "freeTextFieldOne") || "Audit Title")
        }${
          getFieldValidation(configuration, "freeTextFieldOne", "projectBasic")
            ? "*"
            : ""
        }`,
        multiline: false,
        name: "freeTextFieldOne",
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        sx: STYLE_TEXT,
        isVisible: () =>
          configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "projectBasic")
            ?.find((f) => f.fieldName === "freeTextFieldOne")?.enabled ?? false,
      });

      overallTab.addFormField({
        type: "dropdown",
        label: `${
          configuration?.fieldsDefinition
            ?.filter((field) => field.purpose === "projectBasic")
            ?.find(
              (f) =>
                f.fieldName === "secondCategory" &&
                f.enabled === true &&
                (configuration?.categoryDefinition?.length ?? 0) > 0
            )?.label ??
          (getLocalizedMessage(language, "category") || "Category")
        }${
          getFieldValidation(configuration, "secondCategory", "projectBasic")
            ? "*"
            : ""
        }`,
        name: `secondCategory`,
        uiOptions: {
          fieldSize: { xs: 6 },
        },
        sx: STYLE_TEXT,
        menuItemsSx: STYLE_TEXT,
        isVisible: () =>
          Boolean(
            configuration?.fieldsDefinition
              ?.filter((field) => field.purpose === "projectBasic")
              ?.find((f) => f.fieldName === "secondCategory")?.enabled
          ),
        items: toDropdownItems(configuration?.secondCategoryDefinition),
      });
    });
  });
}
